import { useState, useRef, useEffect, useCallback } from 'react'
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CloudUpload,
  FileText,
  History,
  Key,
  Loader2,
  RefreshCw,
  Settings,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { INGESTION_API } from '@/api/client'
import { Step3IngestForm, IngestResultCard } from '../components/Step3IngestForm'
import type {
  ContentType,
  DataService,
  IngestCredentials,
  IngestResponse,
  PresignedUrlResponse,
} from '../types'

const CREDS_KEY = 'fh_ingestion_credentials'

function getStorageProvider(url: string): string {
  if (!url) return 'Cloud Storage'
  if (url.includes('amazonaws.com') || url.includes('s3.')) return 'AWS S3'
  if (url.includes('googleapis.com') || url.includes('storage.google')) return 'Google Cloud Storage'
  if (url.includes('blob.core.windows.net')) return 'Azure Blob Storage'
  return 'Cloud Storage'
}

const MOCK_PRESIGNED: PresignedUrlResponse = {
  presigned_url: 'https://mock-s3.example.com/upload/demo-file',
  file_path: 'uploads/demo/customers_2024_01.csv',
  bucket_name: 'fh-ingestion-demo',
  content_type: 'text/csv',
  expires_in_seconds: 3600,
}

type Step = 1 | 2 | 3

function loadCreds(): IngestCredentials {
  try {
    return JSON.parse(localStorage.getItem(CREDS_KEY) || 'null') ?? { keyId: '', secret: '' }
  } catch {
    return { keyId: '', secret: '' }
  }
}

async function parseCSVHeaders(file: File): Promise<string[]> {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = (e.target?.result as string) ?? ''
      const firstLine = text.split(/\r?\n/)[0] ?? ''
      const headers = firstLine.split(',').map((h) => h.trim().replace(/^"|"$/g, ''))
      resolve(headers.filter(Boolean))
    }
    reader.readAsText(file.slice(0, 8192))
  })
}

export function DataIngestionPage() {
  const [activeTab, setActiveTab] = useState<'ingest' | 'history'>('ingest')
  const [demoMode, setDemoMode] = useState(false)

  // credentials
  const [credentials, setCredentials] = useState<IngestCredentials>(loadCreds)
  const [showCreds, setShowCreds] = useState(() => !loadCreds().keyId)
  const [credsKeyId, setCredsKeyId] = useState(() => loadCreds().keyId)
  const [credsSecret, setCredsSecret] = useState(() => loadCreds().secret)

  // wizard state
  const [currentStep, setCurrentStep] = useState<Step>(1)

  // step 1
  const [contentType, setContentType] = useState<ContentType>('customer')
  const [fileName, setFileName] = useState('')
  const [step1Loading, setStep1Loading] = useState(false)
  const [step1Result, setStep1Result] = useState<PresignedUrlResponse | null>(null)
  const [step1Error, setStep1Error] = useState<string | null>(null)

  // step 2
  const [step2Mode, setStep2Mode] = useState<'upload' | 'specify'>('upload')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [csvHeaders, setCsvHeaders] = useState<string[]>([])
  const [csvDataTypes, setCsvDataTypes] = useState<Record<string, string>>({})
  const [step2Loading, setStep2Loading] = useState(false)
  const [step2Done, setStep2Done] = useState(false)
  const [step2Error, setStep2Error] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // specify path mode
  const [specifyBucketName, setSpecifyBucketName] = useState('')
  const [specifyFilePath, setSpecifyFilePath] = useState('')
  const [specifyService, setSpecifyService] = useState<DataService>('gcp')
  const [specifyDone, setSpecifyDone] = useState(false)
  const [headerFetchLoading, setHeaderFetchLoading] = useState(false)
  const [headerFetchError, setHeaderFetchError] = useState<string | null>(null)
  const specifyFileRef = useRef<HTMLInputElement>(null)

  // step 3
  const [step3Result, setStep3Result] = useState<IngestResponse | null>(null)

  const apiHeaders = () => ({
    'Content-Type': 'application/json',
    'X-FH-Key-ID': credentials.keyId,
    'X-FH-Key-Secret': credentials.secret,
  })

  const hasCredentials = Boolean(credentials.keyId && credentials.secret)

  const handleSaveCreds = () => {
    const creds: IngestCredentials = { keyId: credsKeyId.trim(), secret: credsSecret.trim() }
    localStorage.setItem(CREDS_KEY, JSON.stringify(creds))
    setCredentials(creds)
    setShowCreds(false)
  }

  // step 1
  const handleStep1 = async () => {
    if (!fileName.trim()) return
    setStep1Loading(true)
    setStep1Error(null)
    setStep1Result(null)
    try {
      if (demoMode) {
        await new Promise((r) => setTimeout(r, 600))
        const mock = { ...MOCK_PRESIGNED, file_path: `uploads/demo/${fileName.trim()}` }
        setStep1Result(mock)
        setCurrentStep(2)
        return
      }
      const res = await fetch(`${INGESTION_API}/upload/presigned-url`, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify({ file_name: fileName.trim(), content_type: contentType }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
      setStep1Result(data as PresignedUrlResponse)
      setCurrentStep(2)
    } catch (err) {
      setStep1Error(err instanceof Error ? err.message : 'Request failed')
    } finally {
      setStep1Loading(false)
    }
  }

  // step 2 — upload mode
  const handleFileSelect = async (file: File) => {
    setSelectedFile(file)
    setStep2Error(null)
    setStep2Done(false)
    const headers = await parseCSVHeaders(file)
    setCsvHeaders(headers)
  }

  const handleStep2Upload = async () => {
    if (!step1Result || !selectedFile) return
    setStep2Loading(true)
    setStep2Error(null)
    try {
      if (demoMode) {
        await new Promise((r) => setTimeout(r, 800))
        setStep2Done(true)
        setCurrentStep(3)
        return
      }
      // S3 pre-signs with content-disposition in SignedHeaders — must be included in the PUT.
      // GCS / Azure pre-signed URLs do not require this header.
      const uploadHeaders: Record<string, string> = { 'Content-Type': step1Result.content_type }
      if (step1Result.presigned_url.includes('amazonaws.com')) {
        uploadHeaders['Content-Disposition'] = 'inline'
      }
      const res = await fetch(step1Result.presigned_url, {
        method: 'PUT',
        headers: uploadHeaders,
        body: selectedFile,
      })
      if (!res.ok) throw new Error(`Upload failed: HTTP ${res.status}`)
      setStep2Done(true)
      setCurrentStep(3)
    } catch (err) {
      setStep2Error(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setStep2Loading(false)
    }
  }

  // step 2 — fetch headers from cloud storage
  const handleFetchHeaders = async () => {
    if (!specifyBucketName.trim() || !specifyFilePath.trim()) return
    setHeaderFetchLoading(true)
    setHeaderFetchError(null)
    try {
      const res = await fetch(`${INGESTION_API}/upload/file-headers`, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify({
          service: specifyService,
          bucket: specifyBucketName.trim(),
          filePath: specifyFilePath.trim(),
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
      if (data.headers?.length) {
        setCsvHeaders(data.headers)
        if (data.dataTypes && Object.keys(data.dataTypes).length > 0) {
          setCsvDataTypes(data.dataTypes)
        }
      } else {
        setHeaderFetchError('No headers found in the file.')
      }
    } catch (err) {
      setHeaderFetchError(err instanceof Error ? err.message : 'Failed to fetch headers')
    } finally {
      setHeaderFetchLoading(false)
    }
  }

  // step 2 — specify mode
  const handleStep2Specify = () => {
    if (!specifyBucketName.trim() || !specifyFilePath.trim()) return
    setSpecifyDone(true)
    setStep2Done(true)
    setCurrentStep(3)
  }

  // reset
  const handleReset = () => {
    setCurrentStep(1)
    setFileName('')
    setStep1Result(null)
    setStep1Error(null)
    setSelectedFile(null)
    setCsvHeaders([])
    setCsvDataTypes({})
    setStep2Mode('upload')
    setStep2Done(false)
    setStep2Error(null)
    setSpecifyBucketName('')
    setSpecifyFilePath('')
    setSpecifyService('gcp')
    setSpecifyDone(false)
    setHeaderFetchLoading(false)
    setHeaderFetchError(null)
    setStep3Result(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (specifyFileRef.current) specifyFileRef.current.value = ''
  }

  // derived values for step 3
  const activeFilePath = step1Result?.file_path ?? (specifyDone ? specifyFilePath : '')
  const activeBucketName = step1Result?.bucket_name ?? (specifyDone ? specifyBucketName : '')

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Data Ingestion</h1>
          <p className="text-gray-500 mt-1">
            Upload and process customer, event, or entity data using the FH Ingestion API
          </p>
        </div>
        <button
          onClick={() => setDemoMode((v) => !v)}
          className={cn(
            'mt-1 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors',
            demoMode
              ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
              : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200',
          )}
        >
          {demoMode ? '⚡ Demo Mode ON' : 'Demo Mode'}
        </button>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('ingest')}
          className={cn(
            'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            activeTab === 'ingest'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700',
          )}
        >
          <CloudUpload className="h-4 w-4" />
          New Ingestion
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={cn(
            'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            activeTab === 'history'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700',
          )}
        >
          <History className="h-4 w-4" />
          Ingestion History
        </button>
      </div>

      {activeTab === 'history' && (
        <IngestionHistoryTab credentials={credentials} />
      )}

      {activeTab === 'ingest' && <>

      {demoMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 text-sm text-amber-800">
          <strong>Demo Mode</strong> — API calls are mocked. No real data is sent or stored.
        </div>
      )}

      {/* Credentials Bar */}
      {showCreds ? (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Key className="h-5 w-5 text-primary" />
            <h2 className="font-semibold text-gray-900">API Credentials</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Enter your Ingestion API key credentials. Stored in browser only.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Key ID</label>
              <input
                type="text"
                value={credsKeyId}
                onChange={(e) => setCredsKeyId(e.target.value)}
                placeholder="fh_xxx"
                className="w-full font-mono text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">API Secret</label>
              <input
                type="password"
                value={credsSecret}
                onChange={(e) => setCredsSecret(e.target.value)}
                placeholder="sk_xxx"
                className="w-full font-mono text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSaveCreds}
              disabled={!credsKeyId.trim() || !credsSecret.trim()}
              className="px-4 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Save Credentials
            </button>
            {hasCredentials && (
              <button
                onClick={() => setShowCreds(false)}
                className="px-4 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-6">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-sm text-green-800 font-medium">Credentials configured</span>
            <span className="font-mono text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded">
              {credentials.keyId}
            </span>
          </div>
          <button
            onClick={() => setShowCreds(true)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <Settings className="h-4 w-4" />
            Change
          </button>
        </div>
      )}

      {/* Step Progress */}
      <div className="flex items-center mb-8">
        {([1, 2, 3] as Step[]).map((step, idx) => (
          <div key={step} className="flex items-center">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentStep(step)}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold border-2 transition-colors cursor-pointer',
                  currentStep === step
                    ? 'border-primary bg-primary text-white'
                    : currentStep > step
                      ? 'border-green-500 bg-green-500 text-white hover:bg-green-400'
                      : 'border-gray-300 bg-white text-gray-400 hover:border-primary/50',
                )}
              >
                {currentStep > step ? <CheckCircle2 className="h-4 w-4" /> : step}
              </button>
              <span
                className={cn(
                  'text-sm font-medium hidden sm:block',
                  currentStep === step
                    ? 'text-primary'
                    : currentStep > step
                      ? 'text-green-600'
                      : 'text-gray-400',
                )}
              >
                {step === 1
                  ? 'Generate Pre-signed URL'
                  : step === 2
                    ? step2Mode === 'specify'
                      ? 'Specify File Path'
                      : `Upload to ${getStorageProvider(step1Result?.presigned_url ?? '')}`
                    : 'Trigger Ingestion'}
              </span>
            </div>
            {idx < 2 && <ChevronRight className="h-5 w-5 text-gray-300 mx-3 shrink-0" />}
          </div>
        ))}
        {(step1Result || step2Done || step3Result) && (
          <button
            onClick={handleReset}
            className="ml-auto flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Start Over
          </button>
        )}
      </div>

      {/* ── Step 1 ── */}
      <StepCard
        step={1}
        currentStep={currentStep}
        done={!!step1Result}
        summary={step1Result ? step1Result.file_path : undefined}
        onEdit={() => setCurrentStep(1)}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
            <div className="flex gap-6">
              {(['customer', 'event', 'entity'] as ContentType[]).map((ct) => (
                <label key={ct} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="contentType"
                    value={ct}
                    checked={contentType === ct}
                    onChange={() => setContentType(ct)}
                    className="accent-primary"
                  />
                  <span className="text-sm text-gray-700 capitalize">{ct}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              File Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleStep1()}
              placeholder="customers_2024_01.csv"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-gray-400 mt-1">Must include extension: .csv or .xlsx</p>
          </div>

          {step1Error && <ErrorBanner message={step1Error} />}

          <button
            onClick={handleStep1}
            disabled={step1Loading || !fileName.trim() || (!hasCredentials && !demoMode)}
            className="px-5 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {step1Loading ? 'Generating…' : 'Generate Pre-signed URL'}
          </button>

          {step1Result && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm space-y-1">
              <p className="font-medium text-green-800">Pre-signed URL generated</p>
              <p className="text-green-700 font-mono text-xs break-all">{step1Result.file_path}</p>
              <p className="text-green-600 text-xs">
                Bucket: {step1Result.bucket_name} · Expires in {step1Result.expires_in_seconds}s
              </p>
            </div>
          )}
        </div>
      </StepCard>

      {/* ── Step 2 ── */}
      <StepCard
        step={2}
        currentStep={currentStep}
        done={step2Done}
        label={
          step2Mode === 'specify'
            ? 'Specify File Path'
            : `Upload to ${getStorageProvider(step1Result?.presigned_url ?? '')}`
        }
        summary={
          step2Done
            ? step2Mode === 'specify'
              ? `${specifyFilePath} (${specifyBucketName})`
              : selectedFile
                ? `${selectedFile.name} (${csvHeaders.length} headers)`
                : undefined
            : undefined
        }
        onEdit={() => setCurrentStep(2)}
      >
        <div className="space-y-4">
          {/* Mode toggle */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden w-fit text-sm">
            <button
              onClick={() => setStep2Mode('upload')}
              className={cn(
                'px-4 py-2 transition-colors',
                step2Mode === 'upload'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50',
              )}
            >
              Upload File
            </button>
            <button
              onClick={() => setStep2Mode('specify')}
              className={cn(
                'px-4 py-2 transition-colors border-l border-gray-200',
                step2Mode === 'specify'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50',
              )}
            >
              Specify Path
            </button>
          </div>

          {/* Upload mode */}
          {step2Mode === 'upload' && (
            <>
              {!step1Result ? (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                  Complete Step 1 to get a pre-signed upload URL, or switch to{' '}
                  <button
                    onClick={() => setStep2Mode('specify')}
                    className="font-semibold underline hover:text-amber-900"
                  >
                    Specify Path
                  </button>{' '}
                  mode.
                </div>
              ) : (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                    <p className="text-blue-800 font-medium">
                      Uploading to {getStorageProvider(step1Result.presigned_url)}
                    </p>
                    <p className="text-blue-700 font-mono text-xs mt-1 break-all">
                      {step1Result.file_path}
                    </p>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleFileSelect(file)
                    }}
                    className="hidden"
                  />
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={cn(
                      'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
                      selectedFile
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-300 hover:border-gray-400 bg-gray-50',
                    )}
                  >
                    {selectedFile ? (
                      <div>
                        <FileText className="h-10 w-10 text-green-600 mx-auto mb-2" />
                        <p className="font-medium text-gray-900">{selectedFile.name}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                          {csvHeaders.length > 0 && ` · ${csvHeaders.length} headers`}
                        </p>
                        {csvHeaders.length > 0 && (
                          <p className="text-xs text-green-600 mt-1">
                            {csvHeaders.slice(0, 4).join(', ')}
                            {csvHeaders.length > 4 && ` +${csvHeaders.length - 4} more`}
                          </p>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedFile(null)
                            setCsvHeaders([])
                            if (fileInputRef.current) fileInputRef.current.value = ''
                          }}
                          className="mt-2 text-xs text-red-600 hover:text-red-700 flex items-center gap-1 mx-auto"
                        >
                          <X className="h-3 w-3" /> Remove
                        </button>
                      </div>
                    ) : (
                      <div>
                        <CloudUpload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 font-medium">Click to select file</p>
                        <p className="text-xs text-gray-400 mt-1">.csv or .xlsx</p>
                      </div>
                    )}
                  </div>

                  {step2Error && <ErrorBanner message={step2Error} />}
                  {step2Done && (
                    <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm">
                      <CheckCircle2 className="h-4 w-4" />
                      File uploaded successfully
                    </div>
                  )}

                  <button
                    onClick={handleStep2Upload}
                    disabled={step2Loading || !selectedFile}
                    className="px-5 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {step2Loading
                      ? 'Uploading…'
                      : `Upload to ${getStorageProvider(step1Result.presigned_url)}`}
                  </button>
                </>
              )}
            </>
          )}

          {/* Specify Path mode */}
          {step2Mode === 'specify' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
                  <select
                    value={specifyService}
                    onChange={(e) => setSpecifyService(e.target.value as DataService)}
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="gcp">gcp</option>
                    <option value="aws">aws</option>
                    <option value="azure">azure</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bucket Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={specifyBucketName}
                    onChange={(e) => setSpecifyBucketName(e.target.value)}
                    placeholder="my-ingestion-bucket"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File Path <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={specifyFilePath}
                    onChange={(e) => setSpecifyFilePath(e.target.value)}
                    placeholder="uploads/customers_2024.csv"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              {/* Header detection */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleFetchHeaders}
                  disabled={headerFetchLoading || !specifyBucketName.trim() || !specifyFilePath.trim() || (!hasCredentials && !demoMode)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-primary text-primary rounded-md hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {headerFetchLoading
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Detecting…</>
                    : <><FileText className="h-3.5 w-3.5" /> Detect Headers from Cloud</>}
                </button>
                <span className="text-xs text-gray-400">or</span>
                <input
                  ref={specifyFileRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) parseCSVHeaders(file).then(setCsvHeaders)
                  }}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => specifyFileRef.current?.click()}
                  className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 hover:underline"
                >
                  <FileText className="h-3.5 w-3.5" />
                  Upload local file
                </button>
              </div>

              {headerFetchError && <ErrorBanner message={headerFetchError} />}

              {csvHeaders.length > 0 && (
                <p className="text-xs text-green-600">
                  ✓ {csvHeaders.length} headers detected:{' '}
                  {csvHeaders.slice(0, 4).join(', ')}
                  {csvHeaders.length > 4 && ` +${csvHeaders.length - 4} more`}
                </p>
              )}

              {specifyDone && (
                <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm">
                  <CheckCircle2 className="h-4 w-4" />
                  Path confirmed: {specifyFilePath}
                </div>
              )}

              <button
                onClick={handleStep2Specify}
                disabled={!specifyBucketName.trim() || !specifyFilePath.trim()}
                className="px-5 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Confirm Path
              </button>
            </div>
          )}
        </div>
      </StepCard>

      {/* ── Step 3 ── */}
      <StepCard step={3} currentStep={currentStep} done={!!step3Result}>
        <Step3IngestForm
          initialFilePath={activeFilePath}
          initialBucketName={activeBucketName}
          initialContentType={contentType}
          csvHeaders={csvHeaders}
          initialDataTypes={csvDataTypes}
          credentials={credentials}
          demoMode={demoMode}
          onResult={setStep3Result}
        />
        {step3Result && <IngestResultCard result={step3Result} />}
      </StepCard>

      </>}
    </div>
  )
}

// ── Sub-components ──────────────────────────────────────────────────────────

interface StepCardProps {
  step: Step
  currentStep: Step
  done: boolean
  label?: string
  summary?: string
  onEdit?: () => void
  children?: React.ReactNode
}

function StepCard({ step, currentStep, done: _done, label, summary, onEdit, children }: StepCardProps) {
  const isActive = currentStep === step
  const isPast = currentStep > step

  return (
    <div
      className={cn(
        'rounded-xl border mb-4 transition-all',
        isActive ? 'border-primary/40 shadow-sm' : 'border-gray-200',
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between px-5 py-3 border-b rounded-t-xl',
          isActive ? 'border-primary/20 bg-primary/5' : 'border-gray-100 bg-gray-50',
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold',
              isActive
                ? 'bg-primary text-white'
                : isPast
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-300 text-white',
            )}
          >
            {isPast ? <CheckCircle2 className="h-3.5 w-3.5" /> : step}
          </div>
          <span className="font-semibold text-sm text-gray-800">
            Step {step}:{' '}
            {label ?? (step === 1
              ? 'Generate Pre-signed URL'
              : step === 2
                ? 'Upload File'
                : 'Trigger Ingestion')}
          </span>
          {isPast && summary && (
            <span className="text-xs text-gray-500 font-mono hidden md:block">{summary}</span>
          )}
        </div>
        {isPast && onEdit && (
          <button onClick={onEdit} className="text-xs text-primary hover:underline">
            Edit
          </button>
        )}
      </div>
      {isActive && <div className="p-5">{children}</div>}
    </div>
  )
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm">
      <span className="text-red-600 mt-0.5 shrink-0">⚠</span>
      <p className="text-red-800">{message}</p>
    </div>
  )
}

// ── Ingestion History Tab ────────────────────────────────────────────────────

interface AuditLog {
  id: string
  requestId: string
  accId: number
  filePath: string
  bucketName: string
  contentType: string
  service: string
  apiStatus: string
  rowCount: number | null
  dwStatus: string | null
  dwCount: number | null
  createdAt: string
}

interface AuditPage {
  content: AuditLog[]
  page: number
  size: number
  totalElements: number
  totalPages: number
  last: boolean
}

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'VALIDATION_FAILED', label: 'Validation Failed' },
  { value: 'KAFKA_PUBLISH_FAILED', label: 'Kafka Error' },
]

function IngestionHistoryTab({ credentials }: { credentials: IngestCredentials }) {
  const [data, setData] = useState<AuditPage | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [statusFilter, setStatusFilter] = useState('')

  const apiHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'X-FH-Key-ID': credentials.keyId,
    'X-FH-Key-Secret': credentials.secret,
  }), [credentials])

  const fetchLogs = useCallback(async (p: number, status: string) => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ page: String(p), size: '20' })
      if (status) params.set('status', status)
      const res = await fetch(`${INGESTION_API}/ingest/logs?${params}`, { headers: apiHeaders() })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setData(await res.json())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs')
    } finally {
      setLoading(false)
    }
  }, [apiHeaders])

  useEffect(() => {
    fetchLogs(page, statusFilter)
  }, [fetchLogs, page, statusFilter])

  const handleStatusChange = (s: string) => {
    setStatusFilter(s)
    setPage(0)
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {STATUS_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {data && (
            <span className="text-xs text-gray-500">
              {data.totalElements} record{data.totalElements !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          onClick={() => fetchLogs(page, statusFilter)}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md px-2.5 py-1.5 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && <ErrorBanner message={error} />}

      {/* No credentials */}
      {!credentials.keyId && (
        <div className="text-center py-10 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg">
          Enter API credentials above to load ingestion history.
        </div>
      )}

      {/* Table */}
      {credentials.keyId && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">File</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">API Status</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Rows</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">DW Status</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">DW Count</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                )}
                {!loading && data?.content.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-gray-400 text-sm">
                      No ingestion records found.
                    </td>
                  </tr>
                )}
                {!loading && data?.content.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 max-w-xs">
                      <p className="font-mono text-xs text-gray-700 truncate" title={log.filePath}>
                        {log.filePath}
                      </p>
                      <p className="text-xs text-gray-400">{log.bucketName} · {log.service}</p>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                        {log.contentType}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <ApiStatusBadge status={log.apiStatus} />
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs text-gray-600">
                      {log.rowCount != null ? log.rowCount.toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <DwStatusBadge status={log.dwStatus} />
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs text-gray-600">
                      {log.dwCount != null ? log.dwCount.toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 bg-gray-50">
              <span className="text-xs text-gray-500">
                Page {data.page + 1} of {data.totalPages}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => p - 1)}
                  disabled={data.page === 0}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={data.last}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ApiStatusBadge({ status }: { status: string }) {
  const variants: Record<string, string> = {
    ACCEPTED: 'bg-green-100 text-green-800',
    VALIDATION_FAILED: 'bg-red-100 text-red-800',
    KAFKA_PUBLISH_FAILED: 'bg-orange-100 text-orange-800',
  }
  return (
    <span className={cn('inline-block px-2 py-0.5 rounded text-xs font-medium', variants[status] ?? 'bg-gray-100 text-gray-700')}>
      {status === 'ACCEPTED' ? 'Accepted' : status === 'VALIDATION_FAILED' ? 'Validation Failed' : status}
    </span>
  )
}

function DwStatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-gray-300 italic">pending</span>
  const variants: Record<string, string> = {
    COMPLETED: 'bg-green-100 text-green-800',
    PROCESSING: 'bg-blue-100 text-blue-800',
    FAILED: 'bg-red-100 text-red-800',
    PENDING: 'bg-gray-100 text-gray-600',
  }
  return (
    <span className={cn('inline-block px-2 py-0.5 rounded text-xs font-medium capitalize', variants[status] ?? 'bg-gray-100 text-gray-700')}>
      {status.toLowerCase()}
    </span>
  )
}
